import React, { useEffect, useMemo, useRef, useState } from "react";
import { useToast } from "../contexts/ToastContext";
import { useAuth } from "../hooks/useAuth";
import { useCamera } from "../hooks/useCamera";
import { useNetworkStatus } from "../hooks/useNetworkStatus";
import { usePhotoQueue } from "../hooks/usePhotoQueue";
import { photoStorageService } from "../services/photoStorageService";
import { describeSyncError, syncService } from "../services/syncService";
import { Photo, SyncStatus } from "../types/Photo";
import { productIdFor } from "../utils/product";

interface CaptureProps {
  /** Le produit ouvert à l'entrée : neuf par « Démarrer », existant par « Ajouter des photos ». */
  initialProduct: string;
  /** « Terminer » : le produit clos, on revient aux produits. */
  onFinish: () => void;
}

/**
 * Une vignette de la pellicule : la photo, rien d'autre. La suppression est un
 * geste d'exception — elle vit dans la vue de détail du produit, où la grille
 * lui laisse la place d'une confirmation ; sous le viseur, un bouton de plus au
 * bord du pouce est un déclenchement de moins qui atteint sa cible.
 */
const FilmstripThumbnail: React.FC<{ photo: Photo }> = ({ photo }) => {
  const imageUrl = useMemo(
    () => URL.createObjectURL(photo.thumbnailBlob),
    [photo.thumbnailBlob]
  );

  useEffect(() => () => URL.revokeObjectURL(imageUrl), [imageUrl]);

  return (
    <img
      src={imageUrl}
      alt={photo.filename}
      className="h-16 w-16 shrink-0 rounded-md object-cover"
    />
  );
};

const Capture: React.FC<CaptureProps> = ({ initialProduct, onFinish }) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const isOnline = useNetworkStatus();
  const { showToast } = useToast();
  const { user } = useAuth();
  const { isActive, error, loading, start, stop, capture } = useCamera();
  const { photos, addPhoto } = usePhotoQueue(user);
  // Le produit en cours de prise de vue. « Produit suivant » en ouvre un autre
  // sans quitter le viseur : c'est ce qui permet d'enchaîner les produits.
  const [productId, setProductId] = useState(initialProduct);

  // Ne sont sous le viseur que les photos du produit ouvert. Celles des autres
  // restent dans la file — elles partent au prochain envoi — mais elles ne sont
  // pas de ce produit-ci, donc elles ne s'y montrent pas.
  const productPhotos = photos.filter((p) => p.productId === productId);

  // Ce que la clôture enverra : toute la file sauf ce qui est déjà arrivé. Les
  // photos envoyées restent sur l'appareil jusqu'à la purge des sept jours ;
  // les recompter ici les ferait repartir à chaque clôture.
  const unsentPhotos = photos.filter((p) => p.syncStatus !== SyncStatus.SYNCED);

  useEffect(() => {
    let mounted = true;
    let startTimeout: NodeJS.Timeout;

    const initCamera = async () => {
      // Small delay to ensure previous cleanup is complete
      startTimeout = setTimeout(async () => {
        if (videoRef.current && mounted) {
          try {
            await start(videoRef.current);
          } catch (err) {
            console.error("Failed to start camera:", err);
          }
        }
      }, 100);
    };

    initCamera();

    return () => {
      mounted = false;
      if (startTimeout) {
        clearTimeout(startTimeout);
      }
      // Delay stop to allow async start to check mounted flag
      setTimeout(() => stop(), 50);
    };
    // `start` et `stop` sont des `useCallback` sans dépendance : les nommer ici
    // dit la vérité sans jamais rallumer la caméra.
  }, [start, stop]);

  /**
   * Rien ne plafonne le nombre de photos : la borne, c'est la rétention de sept
   * jours. Un lot compté bloquait le viseur au cinquantième déclenchement pour
   * économiser deux millièmes du quota de l'appareil.
   */
  const handleCapture = async () => {
    try {
      const { photoBlob, thumbnailBlob } = await capture();
      await addPhoto(photoBlob, thumbnailBlob, productId);
      // Le déclenchement est un réveil : la photo est déjà à la file, l'envoi
      // part maintenant et court pendant que l'opérateur cadre la suivante.
      // Rien n'est attendu — toute la file repart, pas seulement cette photo.
      if (user) void syncService.startSync(user);

      // Visual feedback (flash)
      const flash = document.createElement("div");
      flash.className = "fixed inset-0 bg-white z-50 animate-flash";
      document.body.appendChild(flash);
      setTimeout(() => flash.remove(), 100);
    } catch (err) {
      console.error("Capture failed:", err);
      showToast("Échec de la capture photo.", "error");
    }
  };

  /**
   * Clore un produit, c'est le mettre à la file d'envoi : il n'y a plus de
   * bouton « envoyer », et plus d'attente non plus. La file reprend tout ce qui
   * n'est pas arrivé, pas seulement ce produit — une photo restée en échec sur
   * un produit plus ancien repart ainsi d'elle-même, sans qu'aucun geste n'ait
   * à la rechercher. Rien n'est trié : toute photo prise pour un produit part
   * avec lui.
   *
   * Un produit incomplet part quand même : le rappel ne contrôle rien, et le
   * prix d'une étiquette manquante se paie au contrôle, pas au viseur.
   *
   * Rien n'est attendu, et aucun succès n'est annoncé : l'issue se lit après
   * coup sur l'indicateur permanent et sur la pastille du produit à l'accueil.
   * Un « 3 photo(s) envoyée(s) » à chaque produit était le bruit dans lequel se
   * perdait le message qui disait « échec ».
   */
  const enqueueProduct = () => {
    if (!user) {
      showToast("Aucun compte actif.", "error");
      return;
    }

    // Un produit sans photo n'existe pas : il n'y a rien à clore ni à envoyer.
    if (unsentPhotos.length === 0) return;

    // Les photos d'aujourd'hui sont déjà en attente d'envoi — elles y sont
    // entrées au déclenchement. Ce marquage ne rattrape plus que celles qu'une
    // version antérieure a laissées en « capturée » sur l'appareil ; sa garde
    // `CAPTURED` protège la preuve : une photo déjà arrivée ne peut pas être
    // renvoyée en attente.
    photoStorageService
      .markPhotosForUpload(unsentPhotos.map((p) => p.id))
      // Toute la file repart, pas seulement le produit clos : une photo en
      // échec d'avant-hier part avec celle qu'on vient de prendre.
      .then(() => syncService.startSync(user))
      .catch((err) =>
        showToast(`Erreur lors de l'envoi : ${describeSyncError(err)}`, "error")
      );
  };

  /**
   * Le geste qui enchaîne les produits : le produit se clôt, part à la file, et
   * un autre s'ouvre à l'instant sans que le viseur bouge. Rien n'est attendu —
   * une photo prise dans la foulée appartient déjà au produit suivant.
   */
  const nextProduct = () => {
    setProductId(productIdFor(new Date()));
    enqueueProduct();
  };

  /** L'accueil revient tout de suite ; l'envoi se poursuit derrière. */
  const finish = () => {
    enqueueProduct();
    onFinish();
  };

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-full p-6 text-center">
        <div className="bg-error/20 text-error p-4 rounded-full mb-4">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-12 w-12"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
        </div>
        <h3 className="text-xl font-bold mb-2">Erreur de caméra</h3>
        <p className="text-base-content/70 mb-6">{error}</p>
        <button
          className="btn btn-primary"
          onClick={() => window.location.reload()}
        >
          Réessayer
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full bg-base-200 overflow-hidden relative">
      {/* Offline Indicator */}
      {!isOnline && (
        <div className="bg-warning text-warning-content text-center py-1 text-xs font-bold z-30">
          Mode hors-ligne
        </div>
      )}

      {/* La caméra prend tout ce qui reste : plus de moitié d'écran en dur, donc
          plus de pourcentage à choisir pour ce qu'il y a — ou non — en dessous. */}
      <div className="relative flex-1 min-h-0 w-full bg-black overflow-hidden">
        {loading && (
          <div className="absolute inset-0 flex items-center justify-center z-10 bg-black/50">
            <span className="loading loading-spinner loading-lg text-primary"></span>
          </div>
        )}
        <video
          ref={videoRef}
          autoPlay
          playsInline
          muted
          className={`h-full w-full object-cover ${!isActive ? "hidden" : ""}`}
        />

        {/* Le seul libellé posé sur l'image : une pastille au fond opaque, donc
            lisible même sur une étiquette blanche en plein soleil. */}
        <div className="absolute top-4 left-4 z-20">
          <div
            className={`badge ${isActive ? "badge-success" : "badge-ghost"} gap-2 p-3 shadow-md`}
          >
            <div
              className={`w-2 h-2 rounded-full ${isActive ? "bg-white animate-pulse" : "bg-gray-400"}`}
            ></div>
            {isActive ? "En direct" : "Initialisation..."}
          </div>
        </div>
      </div>

      {/* La pellicule : une seule rangée, qui n'existe pas tant qu'aucune photo
          n'existe. À zéro photo la caméra a tout l'écran ; à quatre elle cède
          une rangée. Les vignettes défilent horizontalement et ne repassent
          jamais à la ligne — la géométrie ne saute pas au cinquième déclenchement. */}
      {productPhotos.length > 0 && (
        <div className="flex shrink-0 gap-2 overflow-x-auto bg-base-300 px-3 py-2">
          {productPhotos.map((photo) => (
            <FilmstripThumbnail key={photo.id} photo={photo} />
          ))}
        </div>
      )}

      {/* Clôture : le rappel est passif — il n'exige rien, ne coche rien,
          ne bloque rien. Un produit incomplet peut partir. */}
      <div className="shrink-0 p-3 border-t border-base-300 bg-base-100">
        <div className="flex justify-center mb-2">
          {/* L'anneau est teinté, pas blanc : un cercle blanc cerclé de blanc
              disparaît sur le `base-100` du thème clair. */}
          <button
            type="button"
            className="w-16 h-16 rounded-full bg-white ring-4 ring-primary/70 shadow-xl flex items-center justify-center shrink-0 active:scale-95 transition-transform disabled:opacity-50"
            onClick={handleCapture}
            disabled={!isActive || loading}
            aria-label="Prendre une photo"
          >
            <span className="w-12 h-12 rounded-full border-2 border-black/20"></span>
          </button>
        </div>
        <p className="text-xs text-center text-base-content/70 mb-2">
          Produit · date limite · lot
        </p>
        <div className="flex gap-2">
          {/* Jamais désactivés, jamais de compteur : la clôture rend la main
              tout de suite, il n'y a donc rien à attendre sur ces boutons. */}
          <button className="btn btn-primary flex-1" onClick={nextProduct}>
            Produit suivant
          </button>
          <button className="btn btn-outline flex-1" onClick={finish}>
            Terminer
          </button>
        </div>
      </div>
    </div>
  );
};

export default Capture;
